import * as React from 'react'

import { styled, mq, Text, ThemeInterface } from '@thg-commerce/gravity-theme'
import {
  breakpointUtils,
  BreakpointKey,
} from '@thg-commerce/gravity-theme/breakpoints'
import { Image } from '@thg-commerce/gravity-elements/Image/Image'

export type Size = 'small' | 'large'

export interface AuthorImageProps {
  readonly url?: string
  readonly alt: string
  readonly authorName: string
  readonly lazyImage?: boolean
  readonly isAmp?: boolean
  readonly sizes?: Size[]
}

export const sizeSmall = 56
export const sizeLarge = 98

const imageSize = (size: Size) => {
  switch (size) {
    case 'small':
      return sizeSmall
    case 'large':
      return sizeLarge
  }
}

const buildSizesMq = (sizes: Size[], theme: ThemeInterface) => {
  return sizes.map(
    (size, index) => `
      ${mq(
        theme.breakpointUtils.map,
        breakpointUtils.keys[index] as BreakpointKey,
      )} {
        width: ${imageSize(size)}px;
        height: ${imageSize(size)}px;
      }
    `,
  )
}

const AuthorImagePlaceHolder = styled.div<{ sizes: Size[] }>`
  ${Text('medium1', 'alternate')};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.palette.greys.light};
  color: ${(props) => props.theme.colors.palette.greys.dark};

  ${(props) => buildSizesMq(props.sizes, props.theme)};
`

const StyledImage = styled(Image)<{ sizes: Size[] }>`
  ${(props) => buildSizesMq(props.sizes, props.theme)};
  border-radius: 50%;

  img,
  amp-img {
    width: 100%;
    height: 100%;
  }
`

const getInitials = (name: string) =>
  name
    .split(' ')
    .map((part, index, parts) =>
      index === 0 || index === parts.length - 1 ? part[0] : null,
    )
    .join('')

export const AuthorImage = ({
  url,
  alt,
  authorName,
  lazyImage,
  isAmp,
  sizes = ['small'],
}: AuthorImageProps) => {
  const smallestSize = imageSize(sizes[0])
  return url && url.length > 0 ? (
    <StyledImage
      sizes={sizes}
      alt={alt}
      src={[
        {
          url,
          width: smallestSize,
        },
      ]}
      width={smallestSize}
      height={smallestSize}
      isAmp={isAmp || false}
      displayStyle={'rounded'}
      lazy={lazyImage}
      title={authorName}
      imageStyle="cover"
    />
  ) : (
    <AuthorImagePlaceHolder sizes={sizes}>
      {getInitials(authorName)}
    </AuthorImagePlaceHolder>
  )
}
